import React, { useContext } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import AwesomeError from 'components/Common/AwesomeError'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import type { OfferPageFilterType, OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import BodyText from 'LuxKit/Typography/BodyText'
import ContactContext from 'contexts/contactContext'
import TextButton from 'LuxKit/Button/TextButton'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextLink from 'LuxKit/TextLink'
import { useIsMobileDevice } from 'hooks/useIsMobileDevice'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { EmptyArray } from 'lib/array/arrayUtils'
import useModalElementContext from 'hooks/Modal/useModalElementContext'

interface Props {
  className?: string
  hasError?: boolean
  rooms: Array<OfferPageRoom>
  activeHeaderFilter?: OfferPageFilterType
}

function OfferCalendarNotAvailableMessage(props: Props) {
  const { hasError, className, rooms, activeHeaderFilter } = props
  const intl = useIntl()
  const currentContact = useContext(ContactContext)
  const dispatch = useOfferPageDispatch()
  const modalContext = useModalElementContext()

  const isMobileDevice = useIsMobileDevice()
  const showNewMobileMultipleRoom = isMobileDevice && rooms.length > 1

  const onViewPackagesClick = () => {
    if (showNewMobileMultipleRoom) {
      if (activeHeaderFilter === 'checkin') {
        modalContext.resolve()
        dispatch({ type: OfferPageActions.closeHeaderFilter })
      }
      dispatch({ type: OfferPageActions.toggleFilter, filter: 'multiRoomSelect' })
    } else {
      dispatch({ type: OfferPageActions.toggleSubFilter, filter: 'room' })
    }
  }

  const errorTitle = intl.formatMessage({
    id: 'wq7OxdmMQYy6ZNllEe-F',
    defaultMessage: 'Oh no, an error occurred',
    description: 'Title shown when the calendar encounters an error on the offer detail page',
  })
  const noDatesTitle = intl.formatMessage({
    id: 'kCUfjbekGjpzI_eutVGW',
    defaultMessage: 'No dates available',
    description: 'Title shown when no dates are available for the selected room',
  })

  const phoneNumberLink = <div><TextLink href={`tel:${currentContact.local.number}`} rel="nofollow" wrap="no-wrap">{currentContact.local.humanReadable}</TextLink></div>

  const errorBody = <FormattedMessage
    id="DRcnJGcLjyMCvzpYq3Y6"
    defaultMessage="Our team are working on fixing it now. Try selecting another room type or give our customer support team a call on {phoneNumber}"
    description="Error message with suggestion to select another room or contact support"
    values={{ phoneNumber: phoneNumberLink }}
  />
  const soldOutBody = <FormattedMessage
    id="qgTDKmfVdbVb-goZFynd"
    defaultMessage="Unfortunately this room has sold out. Try selecting another room type or give our customer support team a call on {phoneNumber}"
    description="Sold out message with suggestion to select another room or contact support"
    values={{ phoneNumber: phoneNumberLink }}
  />

  return (
    <AwesomeError
      title={hasError ? errorTitle : noDatesTitle}
      className={className}
    >
      <VerticalSpacer gap={32}>
        <BodyText variant="large">
          {hasError ? errorBody : soldOutBody}
        </BodyText>
        <TextButton kind="primary" onClick={onViewPackagesClick}>
          {intl.formatMessage({
            id: 'UDWaad1G4-BiJcNVCHQK',
            defaultMessage: 'View other rooms',
            description: 'Button text to view other rooms on the hotel or room booking page',
          })}
        </TextButton>
      </VerticalSpacer>
    </AwesomeError>
  )
}

type MappedProps = Pick<Props, 'rooms' | 'activeHeaderFilter'>

export default withOfferPageAndReduxState((state): MappedProps => ({
  rooms: state.rooms ?? EmptyArray,
  activeHeaderFilter: state.activeHeaderFilter,
}), OfferCalendarNotAvailableMessage)
